<template>
    <b-sidebar
        id="sidebar"
        :visible="isSidebarActive"
        bg-variant="white"
        sidebar-class="sidebar-lg"
        shadow
        backdrop
        no-header
        no-close-on-backdrop
        right
        @shown="loadData"
        @hidden="resetForm"
        @change="(val) => $emit('update:is-sidebar-active', val)"
    >
        <template #default="{ hide }">
            <!-- Header -->
            <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
                <h5 class="mb-0 text-capitalize">
                    {{ titleForm }}
                </h5>
                <feather-icon
                    class="ml-1 cursor-pointer"
                    icon="XIcon"
                    size="16"
                    @click="hide"
                />
            </div>

            <!-- BODY -->
            <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
                <!-- Form -->
                <b-form
                    class="p-2"
                    @submit.prevent="handleSubmit(onSubmit)"
                    @reset.prevent="resetForm"
                >
                    <!-- Name -->
                    <validation-provider
                        #default="validationContext"
                        name="name"
                        rules="required"
                    >
                        <b-form-group
                            :label="$t('safr.name')"
                            label-for="name"
                        >
                            <b-form-input
                                id="name"
                                v-model="itemData.name"
                                :placeholder="$t('safr.name')"
                                :state="getValidationState(validationContext)"
                                trim
                            />

                            <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                    <!-- Safr Name -->
                    <validation-provider
                        #default="validationContext"
                        name="safr_name"
                        rules="required"
                    >
                        <b-form-group
                            :label="$t('safr.safr_name')"
                            label-for="name"
                        >
                            <b-form-input
                                id="safr_name"
                                v-model="itemData.safr_name"
                                :state="getValidationState(validationContext)"
                                trim
                            />

                            <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                    <!-- Master account -->
                    <validation-provider
                        #default="validationContext"
                        name="master_account"
                        rules="required"
                    >
                        <b-form-group
                            :label="$t('safr.master_account')"
                            label-for="master_account"
                            :state="getValidationState(validationContext)"
                        >
                            <v-select
                                label="user"
                                v-model="itemData.id_safr_master_account"
                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                :placeholder="$t('safr.placeholderMasterAccount')"
                                :options="safrMasterAccounts"
                                :clearable="false"
                                :reduce="(account) => account.id"
                                input-id="master_account"
                            />

                            <b-form-invalid-feedback :state="getValidationState(validationContext)">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                    <!-- Centro -->
                    <validation-provider
                        #default="validationContext"
                        name="centers"
                        rules="required"
                    >
                        <b-form-group
                            :label="$t('safr.center')"
                            label-for="centers"
                            :state="getValidationState(validationContext)"
                        >
                            <v-select
                                label="name"
                                v-model="itemData.id_center"
                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                :placeholder="$t('safr.placeholderCenter')"
                                :options="centers"
                                :clearable="false"
                                :reduce="(center) => center.id"
                                input-id="centers"
                                @input="getCenterSectors($event)"
                            />

                            <b-form-invalid-feedback :state="getValidationState(validationContext)">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>


                    <!-- Sectors -->
                    <validation-provider
                        #default="validationContext"
                        name="tenants"
                        rules="required"
                    >
                        <b-form-group
                            :label="$t('safr.sector')"
                            label-for="sector"
                            :state="getValidationState(validationContext)"
                        >
                            <v-select
                                label="name"
                                v-model="itemData.id_sector"
                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                :placeholder="$t('safr.placeholderSector')"
                                :options="sectors"
                                :clearable="false"
                                :reduce="(sector) => sector.id"
                                input-id="sector"
                            />

                            <b-form-invalid-feedback :state="getValidationState(validationContext)">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                    <!-- Units -->
                    <validation-provider
                        #default="validationContext"
                        name="units"
                        rules="required"
                    >
                        <b-form-group
                            :label="$t('safr.units')"
                            label-for="units"
                            :state="getValidationState(validationContext)"
                        >
                            <select-paginate
                                v-model="itemData.units"
                                :items="units"
                                input-id="units"
                                :multiple="true"
                            />

                            <b-form-invalid-feedback :state="getValidationState(validationContext)">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider name="send_to_seproban" rules="required" vid="send_to_seproban">
                        <b-form-checkbox
                        id="send_to_seproban"
                        v-model="itemData.send_to_seproban"
                        name="send_to_seproban"
                        value="1"
                        class="my-1 text-capitalize"
                        unchecked-value="0"
                        switch
                        >
                        {{ $t("camera_proxy.send_to_seproban") }}
                        </b-form-checkbox>
                    </validation-provider>

                    <!-- Form Actions -->
                    <div class="d-flex mt-2">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="primary"
                            class="mr-2 text-capitalize"
                            type="submit"
                        >
                            {{ isAdd ? $t('actions.add') :$t('actions.edit') }}
                        </b-button>
                        <b-button
                            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                            type="button"
                            variant="outline-secondary"
                            @click="hide"
                            class="text-capitalize"
                            >
                                {{$t('actions.cancel')}}
                        </b-button>
                    </div>
                </b-form>
            </validation-observer>
        </template>
    </b-sidebar>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import axios from '@/libs/axios';
import SelectPaginate from "../../components/SelectPaginate.vue";

export default {
    components: {
        BSidebar,
        BForm,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,
        BButton,
        vSelect,
        BFormCheckbox,

        // Form Validation
        ValidationProvider,
        ValidationObserver,

        SelectPaginate
    },
    directives: {
        Ripple,
    },
    model: {
        prop: 'isSidebarActive',
        event: 'update:is-sidebar-active',
    },
    props: {
        isSidebarActive: {
            type: Boolean,
            required: true,
        },
        itemEdit: {
            type: Object,
            required: false,
        },
        isAdd: {
            type: Boolean,
            required: true,
        },
        units: {
            typeof: Array,
            required: true
        }
    },
    data() {
        return {
            required,
            alphaNum,
            email,
            positive,
        };
    },
    setup(props, { emit }) {
        const blankData = {
            name: null,
            safr_name: null,
            id_safr_master_account: null,
            units: [],
            id_sector: null,
            send_to_seproban: false,
        };

        onMounted(async () => await fetchLists());

        //Composables
        const toast = useToast()
        const { t } = useI18nUtils()

        //Data
        const centers = ref([]);

        const itemData = ref(JSON.parse(JSON.stringify(blankData)))

        const safrMasterAccounts = ref([]);

        const sectors = ref([]);

        //Computed
        const titleForm = computed(() => props.isAdd ? t('safr.addCamera') : t('safr.editCamera'))

        //Methods
        const contentToast = (message,type) => toast({
            component: ToastificationContent,
            props: {
                title: message,
                icon: 'CheckIcon',
                variant: type
            }
        })

        const fetchLists = async () => {
            let apiCenters = await axios.get('/v1/center');
            let apiSafrMasterAccounts = axios.get('/v1/services/facial-recognition/safr/master-accounts')

            await Promise.all([apiCenters,apiSafrMasterAccounts])
                .then((response) => {
                    let [responseCenters,responseSafrMasterAccount] = response;

                    centers.value = responseCenters.data.data;
                    safrMasterAccounts.value = responseSafrMasterAccount.data.data;
                }).catch(() => {
                        safrMasterAccounts.value = [];
                        sectors.value = [];
                    });
                }

        const getCenterSectors = async (id_center, initIdSector = true) => {
            let requestSector = {
                id_center: [id_center]
            }

            let { data } = await axios.get('/v1/sector', { params: requestSector })

            if (initIdSector) {
                itemData.value.id_sector = null
            }

            sectors.value = data.data
        }

        const onSubmit = async () => {
            try {
                let { data } = (itemData.value.id)
                ? await axios.put(`/v1/services/facial-recognition/safr/cameras/${itemData.value.id}`, { camera: itemData.value })
                : await axios.post('/v1/services/facial-recognition/safr/cameras', { camera: itemData.value })
                let { message, success } = data

                contentToast(message,'success')
                emit('refetch-data')
                emit('update:is-sidebar-active', false)
            } catch (error) {
                let { message } = error.response.data

                contentToast(message,'danger')
            }
        }

        const resetData = () => {
            itemData.value = JSON.parse(JSON.stringify(blankData))
        };

        const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

        const loadData = () => {
            if (!props.isAdd) {
                itemData.value = props.itemEdit;

                itemData.value.units = props.itemEdit.camera.units.map((value) => ({
                    value: value.id,
                    label: value.description,
                    id_tenant: value.router.id_tenant,
                }));
            }
        }

        return {
            itemData,
            onSubmit,

            refFormObserver,
            getValidationState,
            loadData,
            resetForm,
            centers,
            safrMasterAccounts,
            sectors,

            getCenterSectors,
            titleForm
        };
    }
};
</script>
